import { useQueryClient } from '@tanstack/react-query'
import { InfoIcon, Loader2Icon } from 'lucide-react'
import { useState } from 'react'

import { Banner, Button } from 'slab'
import { usePostEmployersAcaReportingConfirmAleStatus } from 'utils/web_api'

import ACAReportingHeader from './ACAReportingHeader'
import AleQuestionSection from './AleQuestionSection'

interface AleStatusConfirmationProps {
  planYear: number
  onConfirmed: (aleStatus: boolean) => void
}

export default function AleStatusConfirmation ({
  planYear,
  onConfirmed
}: AleStatusConfirmationProps) {
  const [aleStatus, setAleStatus] = useState<boolean | null>(null)
  const queryClient = useQueryClient()

  const confirmMutation = usePostEmployersAcaReportingConfirmAleStatus({
    mutation: {
      onSuccess: (_data, variables) => {
        queryClient.invalidateQueries({
          predicate: (query) => {
            const queryKey = query.queryKey as string[]
            return queryKey[0] === '/employers/aca_reporting'
          }
        })
        onConfirmed(variables.data.ale_status)
      }
    }
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    if (aleStatus === null) return

    confirmMutation.mutate({
      data: {
        plan_year: planYear,
        ale_status: aleStatus
      }
    })
  }

  const isSubmitting = confirmMutation.isPending

  return (
    <div className='space-y-6'>
      <ACAReportingHeader planYear={planYear} />

      <div className='bg-white rounded-lg border border-gray-200 overflow-hidden'>
        <div className='bg-gray-50 border-b border-gray-200 px-6 py-4'>
          <div className='flex items-center gap-3'>
            <InfoIcon className='text-gray-500' size={20} />
            <span className='text-sm text-gray-700'>You are not eligible for Thatch's ACA filing services, but Thatch can still provide your 1095 data in a .csv format.</span>
          </div>
        </div>

        <form onSubmit={handleSubmit} className='p-6'>
          <div className='space-y-4'>
            <AleQuestionSection
              aleStatus={aleStatus}
              onAleStatusChange={setAleStatus}
              showFormTypeHint
            />

            {confirmMutation.isError && (
              <Banner type='critical' title='Unable to confirm ALE status'>
                {(confirmMutation.error as Error)?.message ?? 'An error occurred. Please try again.'}
              </Banner>
            )}

            <div className='flex justify-end'>
              <Button
                type='submit'
                disabled={aleStatus === null || isSubmitting}
                variant='primary'
              >
                {isSubmitting
                  ? (
                    <span className='flex items-center gap-2'>
                      <Loader2Icon className='w-4 h-4 animate-spin' />
                      Confirming...
                    </span>
                    )
                  : 'Confirm ALE status'}
              </Button>
            </div>
          </div>
        </form>
      </div>
    </div>
  )
}
